import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { LFlex, LBtn } from "@components";
import { LocalStorageKey, useLocalStorage } from "@helpers/localStorageHelper";
import { NavItems } from "@helpers/navigation";
import { PricingTier } from "@store/generalTypes";
import { isEmpty, round } from "lodash";
import { MarkObj } from "rc-slider/lib/Marks";
import React, { useState } from "react";
import { useNavigate } from "react-router";
import { loginRequest } from "../../../../authConfig";
import { AssessmentBasket } from "../../pages/checkout";
import { PricingSlider } from "./PricingSlider";
import { useAppSelector } from "@app/hooks";

const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD"
});

const getPricingBreakdown = (
    pricingTiers: PricingTier[],
    assessmentQuantity: number
): [number, PricingTier | undefined, TierSummaryItem[], number] => {
    const currentTier = pricingTiers.find(
        ({ minimumQuantity, maximumQuantity }) =>
            assessmentQuantity >= minimumQuantity && assessmentQuantity <= maximumQuantity
    );
    const tierSummaries = pricingTiers.reduce(
        (aggregate, { name, minimumQuantity, maximumQuantity, unitPrice }, idx): TierSummaryItem[] => {
            let quantity = idx === 0 ? minimumQuantity - 1 : 0;
            if (assessmentQuantity > maximumQuantity) {
                quantity += maximumQuantity - minimumQuantity + 1;
            } else if (assessmentQuantity >= minimumQuantity) {
                quantity += assessmentQuantity - minimumQuantity + 1;
            } else {
                return aggregate;
            }

            return [...aggregate, { name, unitPrice, quantity, subTotal: quantity * unitPrice }];
        },
        Array<TierSummaryItem>()
    );

    const total = tierSummaries.reduce((aggregate, { subTotal }) => aggregate + subTotal, 0);
    const roundedUnitPrice = round(total / assessmentQuantity, 2);
    const roundedTotal = assessmentQuantity * roundedUnitPrice;

    return [roundedTotal, currentTier, tierSummaries, roundedUnitPrice];
};

const getMarks = (pricingTiers: PricingTier[]): Record<string | number, React.ReactNode | MarkObj> | {} => {
    return pricingTiers.reduce((marks, tier, idx): { [key: number]: Object } => {
        const label = idx === pricingTiers.length - 1 ? tier.maximumQuantity : tier.minimumQuantity;
        return {
            ...marks,
            [label]: {
                label,
                style: { fontSize: 16, marginTop: "1rem", paddingRight: "2rem" }
            }
        };
    }, {});
};

export default React.memo(PricingSummary);

function PricingSummary({
    pricingTiers = [],
    defaultAssessmentQuantity,
    minimumQuantity,
    maximumQuantity
}: PricingSummaryProps): JSX.Element {
    const [assessmentQuantity, setAssessmentQuantity] = useState(defaultAssessmentQuantity);
    const [isLoading, setIsLoading] = useState(false);
    const [roundedTotal, currentTier, tierSummaries, roundedUnitPrice] = getPricingBreakdown(
        pricingTiers,
        assessmentQuantity
    );
    const organization = useAppSelector((x) => x.organization);

    const { instance } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const navigate = useNavigate();

    const [assessmentBasket, setAssessmentBasket] = useLocalStorage<AssessmentBasket>(LocalStorageKey.AssessmentBasket);

    const marks = getMarks(pricingTiers);

    const handleAssessmentQuantityChanged = (input: number | number[]): void => {
        let sanitizedValue = Number(input);

        if (isNaN(sanitizedValue)) {
            return;
        }

        sanitizedValue = sanitizedValue > maximumQuantity ? maximumQuantity : sanitizedValue;

        setAssessmentQuantity(sanitizedValue);
    };

    const handleInputBlur = (): void => {
        if (assessmentQuantity < minimumQuantity) {
            setAssessmentQuantity(minimumQuantity);
        }
    };

    const handleCheckout = async (): Promise<any> => {
        setIsLoading(true);
        setAssessmentBasket({ ...assessmentBasket, quantity: assessmentQuantity });

        if (isAuthenticated) {
            setAssessmentBasket({ organizationId: organization.id!, quantity: assessmentQuantity });
            navigate(NavItems.Purchase.Checkout.path);
        } else {
            await instance.loginRedirect({ ...loginRequest, redirectUri: NavItems.Purchase.SignupCheckout.path });
        }
    };

    const pricingBreakdownDescription = isEmpty(tierSummaries) ? (
        <p></p>
    ) : (
        <p>
            Your total price (
            {tierSummaries
                .map(({ quantity, unitPrice }) => {
                    return `${quantity} ${quantity === 1 ? "assessment" : "assessments"} x $${unitPrice}`;
                })
                .join(" + ")}
            )
        </p>
    );

    return (
        <div className="my-6 py-5">
            <div className="box">
                <LFlex alignCenter justifyCenter className="has-text-ldark-gray p-4">
                    <p className="has-text-weight-bold is-size-4">
                        How many LASSI-D™ assessments would you like to purchase?
                    </p>
                    <p className="mt-3">
                        Use the slider below to select a quantity between {minimumQuantity} – {maximumQuantity}{" "}
                        assessments
                    </p>
                    <LFlex justifyCenter alignCenter className="minw-90">
                        <PricingSlider
                            assessmentQuantity={assessmentQuantity}
                            defaultAssessmentQuantity={defaultAssessmentQuantity}
                            minimumQuantity={minimumQuantity}
                            maximumQuantity={maximumQuantity}
                            onBlur={handleInputBlur}
                            marks={marks}
                            onChange={handleAssessmentQuantityChanged}
                        />
                        <p className="my-5">
                            <span className="has-text-weight-bold">Tiered pricing</span>
                            {currentTier
                                ? ` (${currentTier.minimumQuantity}-${currentTier.maximumQuantity} assessments): `
                                : ": "}
                            <span className="has-text-success has-text-weight-bold">
                                ${roundedUnitPrice.toFixed(2)}
                            </span>
                            /ea
                        </p>
                        <LFlex row className="is-justify-content-space-between minw-90 mt-5">
                            {pricingBreakdownDescription}
                            <p className="has-text-success has-text-weight-bold is-size-5">
                                {formatter.format(roundedTotal)}
                            </p>
                        </LFlex>
                        <div className="divider minw-90" />
                        <LBtn fullWidth classes="mw-80 mt-4" onClick={handleCheckout} loading={isLoading}>
                            Check out
                        </LBtn>
                    </LFlex>
                </LFlex>
            </div>
        </div>
    );
}

interface PricingSummaryProps {
    pricingTiers: PricingTier[];
    defaultAssessmentQuantity: number;
    minimumQuantity: number;
    maximumQuantity: number;
}

interface TierSummaryItem {
    name: string;
    quantity: number;
    unitPrice: number;
    subTotal: number;
}
